import { PLAYING } from '@disco/constants/MediaStatus'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { TrackId } from 'types/track'
import type { EventSource } from 'types/ui'
import type { TrackOptions } from '@disco/state-management/actions/MediaActions'
import {
  pauseTrack,
  playTrack,
} from '@disco/state-management/actions/MediaActions'
import { selectTrackPlaybackStatus } from '@disco/state-management/selectors/media'
import PlayerButton from '../player-button'
type Props = {
  mediaSource: EventSource
  playbackContext: TrackOptions
  trackId: TrackId
}

/**
 * Player play/pause button component that toggles track playback
 * Automatically switches between play and pause icons based on current playback status
 * Dispatches Redux actions to control track playback with proper context
 * @param mediaSource - Source context for the media playback
 * @param playbackContext - Additional context and options for track playback
 * @param trackId - ID of the track to control
 */
const PlayerPlay = ({ mediaSource, playbackContext, trackId }: Props) => {
  const dispatch = useDispatch()
  const playbackStatus = useSelector(state =>
    selectTrackPlaybackStatus(state, {
      trackId,
    }),
  )
  const handleClick = useCallback(() => {
    if (playbackStatus === PLAYING) {
      dispatch(pauseTrack(trackId))
    } else {
      dispatch(playTrack(trackId, mediaSource, playbackContext))
    }
  }, [dispatch, mediaSource, playbackContext, playbackStatus, trackId])
  return (
    <PlayerButton
      onClick={handleClick}
      type={playbackStatus === PLAYING ? 'pause' : 'play'}
    />
  )
}

export default React.memo<Props>(PlayerPlay)
